import { Box, Flex, FlexProps, useDisclosure } from "@chakra-ui/react";
import React, { useEffect, useRef } from "react";

import { IconButton } from "../../../components";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { isIOS } from "../../../utils/device";
import { VolumeControlV2 } from "../VolumeControlV2";
import { IconMute, IconVolume } from "./theme";

interface AudioControlsV2Props {
  volume: number;
  disabled?: boolean;
  muted: boolean;
  setVolume: (volume: number) => void;
  mute: (muted: boolean) => void;
  isTop?: boolean;
  /**
   * When true, displays a horizontal pill layout with the slider always visible.
   * Used when video is hidden (audio-only mode).
   */
  horizontal?: boolean;
  /**
   * When true, forces the volume slider to be visible (for stories/testing).
   */
  forceOpen?: boolean;
}

export const AudioControlsV2: React.FC<AudioControlsV2Props> = ({
  setVolume,
  mute,
  volume,
  disabled = false,
  muted,
  isTop = false,
  horizontal = false,
  forceOpen = false,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isOpen: isHoverOpen, onOpen, onClose } = useDisclosure();
  const isOpen = forceOpen || isHoverOpen;

  useEffect(() => {
    const closeOnClickOutside = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as HTMLElement)) {
        onClose();
      }
    };

    if (isSmallScreen && isOpen && !horizontal) {
      window.addEventListener("click", closeOnClickOutside);
      return () => window.removeEventListener("click", closeOnClickOutside);
    }
  }, [isSmallScreen, isOpen, horizontal]);

  const isIOSDevice = isIOS();
  const isDisabled = disabled || isIOSDevice;
  const showSlider = isOpen && !isIOSDevice;

  const volumeButton = (
    <IconButton
      aria-label="Mute"
      variant="unstyled"
      w={10}
      h={10}
      minW={10}
      display="flex"
      alignItems="center"
      justifyContent="center"
      icon={muted ? <IconMute size={24} /> : <IconVolume size={24} />}
      isRound
      color="white"
      isDisabled={isDisabled}
      _disabled={{ bg: "transparent", opacity: 0.5, cursor: "not-allowed" }}
      _hover={
        isIOSDevice
          ? { bg: "transparent", opacity: 0.5, cursor: "not-allowed" }
          : undefined
      }
      onClick={() => {
        if (isDisabled) return;
        if (isSmallScreen && !isOpen && !isIOSDevice) {
          onOpen();
        } else {
          mute(!muted);
        }
      }}
    />
  );

  const commonFlexProps: FlexProps = {
    alignItems: "center",
    pointerEvents: isIOSDevice ? "none" : "all",
    onMouseEnter: isIOSDevice ? undefined : onOpen,
    onMouseLeave: isIOSDevice ? undefined : onClose,
    borderRadius: "full",
    transition: isIOSDevice ? "none" : "all 0.2s",
  };

  if (horizontal) {
    return (
      <Box position="relative" w={10} h={10}>
        <Flex
          ref={containerRef}
          {...commonFlexProps}
          position="absolute"
          top={0}
          left={0}
          h={10}
          bg={showSlider ? "gray.800" : undefined}
          pl={showSlider ? 0 : 0}
          pr={showSlider ? 4 : 0}
        >
          {volumeButton}
          {showSlider && (
            <VolumeControlV2
              volume={volume}
              setVolume={setVolume}
              disabled={isDisabled}
              orientation="horizontal"
              w="20"
              h="4"
            />
          )}
        </Flex>
      </Box>
    );
  }

  return (
    <Box position="relative" w={10} h={10}>
      <Flex
        ref={containerRef}
        {...commonFlexProps}
        position="absolute"
        bottom={isTop ? undefined : 0}
        top={isTop ? 0 : undefined}
        left={0}
        right={0}
        direction="column"
        justifyContent="flex-end"
        bg={isOpen ? "gray.800" : undefined}
        pb={isOpen ? 0 : 0}
        pt={isOpen ? 4 : 0}
        px={isOpen ? 2 : 0}
      >
        {showSlider && (
          <VolumeControlV2
            volume={volume}
            setVolume={setVolume}
            disabled={isDisabled}
            orientation="vertical"
            h="16"
            w="4"
          />
        )}
        {volumeButton}
      </Flex>
    </Box>
  );
};
